import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import dayjs from "dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;


export function getTodayDate() {
  return dayjs().tz(userTimezone).utc().format("YYYY-MM-DD");
}

export function getConvertDate(date: any) {
  return dayjs(date).tz(userTimezone).utc();
}

export function getCustomSubtractDate(days: number) {
  return dayjs().tz(userTimezone).utc().subtract(days, "day").format("YYYY-MM-DD");
}
export function getCustomSubtractDateHours(days: number) {
    return dayjs().tz(userTimezone).utc().subtract(days, "day").format("YYYY-MM-DD HH:mm:ss");
  }

  export function getCustomSubtractMonthHours(days: number) {
    return dayjs().tz(userTimezone).utc().subtract(days, "month").format("YYYY-MM-DD HH:mm:ss");
  }

export function getCustomAddDate(days: number) {
    return dayjs().tz(userTimezone).utc().add(days, "day").format("YYYY-MM-DD");
  }
  export function getCustomAddDateHours(days: number) {
    return dayjs().tz(userTimezone).utc().add(days, "day").format("YYYY-MM-DD HH:mm:ss");
  }
  

  export function getWeekDate() {
    return dayjs().tz(userTimezone).utc().day();
  }


  export function getUTCDate(date: any) {
    return dayjs().tz(userTimezone).utc()
  }

// New helpers for date-only scheduling logic
export function getUserTimezone() {
  return userTimezone;
}

/** DB / legacy defaults that should not override the editor's local timezone. */
const UTC_DEFAULT_TIMEZONES = new Set(["GMT", "UTC", "Etc/UTC", "Etc/GMT"]);

export function isDefaultUtcTimezone(tz?: string | null) {
  const normalized = String(tz ?? "").trim();
  return !normalized || UTC_DEFAULT_TIMEZONES.has(normalized);
}

/** Schedule modal default: user's timezone unless a real owner timezone was saved. */
export function resolveScheduleOwnerTimezone(ownerTimezone?: string | null) {
  if (isDefaultUtcTimezone(ownerTimezone)) {
    return getUserTimezone();
  }
  return String(ownerTimezone).trim();
}

// Returns follower's current local date (YYYY-MM-DD) without time, no UTC conversion
export function getLocalTodayDate() {
  return dayjs().tz(userTimezone).format("YYYY-MM-DD");
}

export function isValidDateYYYYMMDD(dateStr?: string | null) {
  if (!dateStr) return false;
  // Check for YYYY-MM-DD format (database format)
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) return false;
  const d = dayjs(dateStr, "YYYY-MM-DD", true);
  return d.isValid();
}

export function compareDateStrings(a: string, b: string) {
  // Lexicographic compare works for YYYY-MM-DD
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

// Is target >= start (inclusive)
export function isOnOrAfter(target: string, start: string) {
  return compareDateStrings(target, start) >= 0;
}

// Is target <= end (inclusive)
export function isOnOrBefore(target: string, end: string) {
  return compareDateStrings(target, end) <= 0;
}

// Returns true if followerDate is within [startDate, archiveDate] (archive inclusive)
export function isActiveForDateRange(
  followerDate: string,
  startDate?: string | null,
  archiveDate?: string | null
) {
  const start = isValidDateYYYYMMDD(startDate || "") ? (startDate as string) : getLocalTodayDate();
  const withinStart = isOnOrAfter(followerDate, start);
  if (!archiveDate || !isValidDateYYYYMMDD(archiveDate)) return withinStart; // Never archive
  return withinStart && isOnOrBefore(followerDate, archiveDate);
}

// Get viewer's local date in YYYY-MM-DD format (viewer's timezone)
export function getViewerLocalDate() {
  const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return dayjs().tz(viewerTimezone).format("YYYY-MM-DD");
}

// Determine viewer status based on viewer's local date and component dates
export function getViewerStatus(component: any) {
  const viewerToday = getViewerLocalDate();
  const { start_date, archive_date, status_expiry } = component;

  // Validate dates exist and are valid
  const hasValidStart = isValidDateYYYYMMDD(start_date);
  const hasValidArchive = isValidDateYYYYMMDD(archive_date);

  // Default behavior: if no dates set, show as active
  if (!hasValidStart && !hasValidArchive) {
    return status_expiry || "active";
  }

  const start = hasValidStart ? start_date : viewerToday; // Default to today if no start
  
  // Check if viewer's date is before start date
  if (viewerToday < start) {
    return "draft"; // Not yet visible
  }

  // Check if viewer's date is after archive date (archive is inclusive)
  if (hasValidArchive && viewerToday > archive_date) {
    return "archive"; // Past expiry
  }

  return "active"; // Within visible window
}

// Check if component should be visible to viewer based on status_expiry and dates
export function shouldShowToViewer(component: any) {
  const storedStatus = String(component?.status ?? "")
    .toLowerCase()
    .trim();
  const expiryStatus = String(component?.status_expiry ?? "")
    .toLowerCase()
    .trim();
  const rawStatus = expiryStatus || storedStatus;
  // Explicit drafts stay private even when a date window is set.
  if (
    storedStatus === "draft" ||
    expiryStatus === "draft" ||
    rawStatus === "hidden" ||
    rawStatus === "deleted" ||
    rawStatus === "pending_publish"
  ) {
    return false;
  }

  const backendStatus = component?.status_expiry;
  const { start_date, archive_date } = component;

  // Check if dates are set
  const hasValidStart = isValidDateYYYYMMDD(start_date);
  const hasValidArchive = isValidDateYYYYMMDD(archive_date);
  const hasDates = hasValidStart || hasValidArchive;

  // If no dates are set, only respect manual status_expiry flag.
  if (!hasDates) {
    // Prefer status_expiry when non-empty; empty string falls through to status
    // (template series bootstrap inserts status=active with no status_expiry).
    const statusExpiryStr =
      backendStatus == null ? "" : String(backendStatus).trim();
    const effectiveStatus = String(
      statusExpiryStr !== "" ? statusExpiryStr : (component?.status ?? ""),
    )
      .toLowerCase()
      .trim();

    return effectiveStatus === "active";
  }

  // If dates are set, apply viewer timezone logic (viewer logic takes precedence)
  const viewerStatus = getViewerStatus(component);
  
  // When dates are set, use viewer's timezone logic
  const shouldShow = viewerStatus === "active";


  return shouldShow;
}

// Format date for UI display (e.g., "22 Oct")
export function formatDateForUI(dateString: string | null | undefined) {
  if (!dateString || !isValidDateYYYYMMDD(dateString)) return null;
  return dayjs(dateString, "YYYY-MM-DD").format("MMM DD");
}

// Format date range for UI display (e.g., "22 Oct - 30 Nov" or "22 Oct - ∞")
export function formatDateRangeForUI(startDate: string | null | undefined, archiveDate: string | null | undefined) {
  const start = formatDateForUI(startDate) || "Today";
  const archive = formatDateForUI(archiveDate) || "∞";
  return `${start} - ${archive}`;
}